import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { semantic, space, typeRole } from '@repo/design-tokens';
import { Button, Icon, Popover, Tag } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';

import type { DraftSaveStatus } from './useDraftSaver';

/**
 * Where the step's draft stands — the title-bar tag that replaces a step's Save once the draft
 * saver serves it (`observers-page-draft`, FR4/FR5, Block A).
 *
 * An app composite of existing design-system parts only: `Tag`, `Icon` and `Button`. Rob's v3
 * Observers page design draws the mint *Draft saved 14:32* tag in this position; the not-saved
 * state is the spec's, built from the same parts. Wizard-level rather than Observers-specific,
 * because autosave is rolling out to every onboarding step.
 *
 * Text, never colour alone: each state has its own words and its own glyph.
 */

/** FR5: the sentence when a failed save carried no reason of its own. */
export const DRAFT_SAVE_FALLBACK_REASON = 'The plan could not be saved.';

export interface DraftStatusTagProps {
  status: DraftSaveStatus;
  /** The time to show in the saved state — the last save this session, else the plan's `modified_at`. */
  savedAt: Date | null;
  /** `failed`: the save's reason, or `null`. `blocked`: the caller's sentence. */
  reason: string | null;
  onRetry: () => void;
  /**
   * Retry's accessible name. Default *Retry saving the draft*. The Equipment step's Retry re-reads
   * the register rather than saving again (`equipment-page-stage`, ruling 3), so it says so.
   */
  retryLabel?: string;
  /**
   * What is being saved (`twin-details-page-shell`, R3). `draft` (the default) is the plan's draft:
   * *Draft saved 14:32*. `twin` is a step that writes the twin record straight away (Twin details,
   * Opening hours): *Saved*, with no time, as frames 03 and 06 draw it, and *Not saved* on failure.
   */
  subject?: 'draft' | 'twin';
  /**
   * The twin does not exist yet (create mode, frame 01): the tag reads only *Not created yet*,
   * whatever `status` says. Nothing is saved, so there is no Retry and nothing to announce.
   */
  notCreated?: boolean;
  /**
   * The Equipment step on an empty register (`equipment-page-start`, R7; frame 01): the tag reads
   * only *Nothing to save yet*, whatever `status` says — the same idle look as `notCreated`, with its
   * own words. `notCreated` wins if both are set.
   */
  nothingToSave?: boolean;
}

/** The words for each subject. One table, so the two subjects cannot drift apart state by state. */
const WORDS = {
  draft: {
    saved: (at: Date | null) => (at ? `Draft saved ${clockTime(at)}` : 'Draft saved'),
    notSaved: 'Draft not saved',
    retry: 'Retry saving the draft',
    fallback: DRAFT_SAVE_FALLBACK_REASON,
  },
  twin: {
    saved: () => 'Saved',
    notSaved: 'Not saved',
    retry: 'Retry saving',
    // The twin record, not the plan, is what this subject saves.
    fallback: 'The twin could not be saved.',
  },
} as const;

/**
 * `HH:MM` on the operator's clock — the design's *Draft saved 14:32*, and the camera's *Picture taken
 * 14:32* (`observers-page-camera-stage`).
 */
export const clockTime = (at: Date): string =>
  at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

const DraftStatusTag: React.FC<DraftStatusTagProps> = ({
  status,
  savedAt,
  reason,
  onRetry,
  retryLabel,
  subject = 'draft',
  notCreated = false,
  nothingToSave = false,
}) => {
  const words = WORDS[subject];
  /** The idle states' words — one branch, two texts (R7). Null when the tag reports a save. */
  const idleText = notCreated ? 'Not created yet' : nothingToSave ? 'Nothing to save yet' : null;
  const idle = idleText !== null;
  const notSaved = status === 'failed' || status === 'blocked';
  const reasonText = notSaved ? (reason ?? words.fallback) : null;

  /*
   * The reason is held to two lines, so it fits the nav bar it sits in
   * (`onboarding-persistent-scene-step-titles`). Where that cuts it, *Show all* opens the whole of it
   * in a Popover, reachable by keyboard and touch, never only on hover. The whole text stays in the
   * reason's own node for a screen reader either way.
   */
  const reasonRef = useRef<HTMLSpanElement | null>(null);
  const showAllRef = useRef<HTMLButtonElement | null>(null);
  const reasonId = useId();
  const [reasonCut, setReasonCut] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  useLayoutEffect(() => {
    const el = reasonRef.current;
    if (!el) {
      setReasonCut(false);
      setReasonOpen(false);
      return undefined;
    }
    const measure = () => setReasonCut(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [reasonText]);

  /*
   * A Retry in progress keeps its button, busy, rather than unmounting it. Unmounted, the focus a
   * keyboard operator put on it would fall to the page, and a retry that failed again would leave
   * them to Tab back through the whole wizard to find it.
   */
  const [retrying, setRetrying] = useState(false);
  useEffect(() => {
    if (status !== 'saving') setRetrying(false);
  }, [status]);
  const showRetry = status === 'failed' || (retrying && status === 'saving');

  /*
   * The live region announces TRANSITIONS only (Block A, criterion 22): into *Not saved*, and from
   * *Not saved* back to *Saved*. A region over the tag itself would read every *Saving…* and every
   * new time aloud — about once a second while someone draws.
   *
   * A failure that follows a Retry is announced again even when its sentence is unchanged: the
   * no-break space alternates, so the region's text always changes and the retry is always heard.
   */
  const [announcement, setAnnouncement] = useState('');
  const wasNotSavedRef = useRef(false);
  const lastStatusRef = useRef<DraftSaveStatus>(status);
  useEffect(() => {
    const previous = lastStatusRef.current;
    lastStatusRef.current = status;
    // Nothing is saved before the twin exists (or while the register is empty), so nothing is
    // announced — and no failure is remembered, or the first save afterwards would announce a
    // recovery nobody saw fail.
    if (idle) {
      wasNotSavedRef.current = false;
      setAnnouncement('');
      return;
    }
    if (notSaved && previous !== status) {
      wasNotSavedRef.current = true;
      const text = `${words.notSaved}: ${reasonText}`;
      setAnnouncement(prev => (prev === text ? `${text} ` : text));
    } else if (status === 'saved' && wasNotSavedRef.current) {
      wasNotSavedRef.current = false;
      setAnnouncement(words.saved(null));
    }
  }, [status, notSaved, reasonText, words, idle]);

  if (idle) {
    return (
      <Wrap data-draft-status={notCreated ? 'not-created' : 'nothing-to-save'}>
        <Tag surface="on-solid" tone="mint" icon={<Icon name="check" size="sm" />}>
          {idleText}
        </Tag>
        {/* The same live region, mounted and empty, so it exists before its first real message. */}
        <span role="status" aria-live="polite" style={visuallyHidden}>
          {announcement}
        </span>
      </Wrap>
    );
  }

  return (
    <Wrap data-draft-status={status}>
      {status === 'saved' && (
        <Tag surface="on-solid" tone="mint" icon={<Icon name="check" size="sm" />}>
          {words.saved(savedAt)}
        </Tag>
      )}
      {status === 'saving' && <Tag surface="on-solid">Saving…</Tag>}
      {notSaved && (
        <>
          <Tag surface="on-solid" tone="error" icon={<Icon name="circle-exclamation" size="sm" />}>
            {words.notSaved}
            {/* Read as one sentence — *Draft not saved: {reason}* — though the reason sits outside the pill. */}
            <span style={visuallyHidden}>{': '}</span>
          </Tag>
          <Reason ref={reasonRef}>{reasonText}</Reason>
          {reasonCut && (
            <>
              <Button
                ref={showAllRef}
                type="button"
                variant="text"
                size="inline"
                aria-haspopup="dialog"
                aria-expanded={reasonOpen}
                aria-controls={reasonId}
                aria-label="Show the whole reason"
                onClick={() => setReasonOpen(open => !open)}
              >
                Show all
              </Button>
              <Popover
                id={reasonId}
                open={reasonOpen}
                onClose={() => setReasonOpen(false)}
                anchorRef={showAllRef}
                title={words.notSaved}
                placement="bottom"
                align="end"
              >
                {reasonText}
              </Popover>
            </>
          )}
        </>
      )}
      {showRetry && (
        <Button
          type="button"
          variant="text"
          size="inline"
          iconLeft={<Icon name="arrows-rotate" size="sm" />}
          aria-label={retryLabel ?? words.retry}
          loading={status === 'saving'}
          onClick={() => {
            setRetrying(true);
            onRetry();
          }}
        >
          Retry
        </Button>
      )}
      <span role="status" aria-live="polite" style={visuallyHidden}>
        {announcement}
      </span>
    </Wrap>
  );
};

export default DraftStatusTag;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${space[2]};
  min-width: 0;
`;

/*
 * The reason sits beside the tag rather than inside it: a server sentence is too long for a pill,
 * and the duplicate-code hint names every clashing code. At most two lines (32 px), so it stays
 * inside the 52 px nav bar; where that cuts it, *Show all* opens the whole reason — never a hover
 * title, which a keyboard or touch operator cannot reach, and which would hide the very codes they
 * need to fix. Layout only.
 */
const Reason = styled.span`
  font-family: ${typeRole.caption1.fontFamily};
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.secondary};
  max-width: 320px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;
